import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { canonicalUrl, metaForPath } from '@/lib/seo';

function upsertMeta(attr: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function removeMeta(attr: 'name' | 'property', key: string) {
  document.head.querySelector(`meta[${attr}="${key}"]`)?.remove();
}

function upsertCanonical(href: string) {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function removeCanonical() {
  document.head.querySelector('link[rel="canonical"]')?.remove();
}

/**
 * Applies the current route's metadata to the document head. The prerender
 * writes the same tags into each static page (`src/lib/seo.ts`); this keeps them
 * correct when the client router changes route without a full page load.
 */
export function DocumentMeta() {
  const { pathname } = useLocation();

  useEffect(() => {
    const meta = metaForPath(pathname);
    const canonical = canonicalUrl(pathname);

    document.title = meta.title;
    upsertMeta('name', 'description', meta.description);
    upsertMeta('property', 'og:title', meta.title);
    upsertMeta('property', 'og:description', meta.description);
    upsertMeta('property', 'og:url', canonical);

    if (meta.noindex) {
      removeCanonical();
      upsertMeta('name', 'robots', 'noindex');
    } else {
      upsertCanonical(canonical);
      removeMeta('name', 'robots');
    }
  }, [pathname]);

  return null;
}
